import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import {
  Figure,
  LoadMore,
  NameLink,
  PersonScopeCard,
  Select,
  TABLE_REGION,
  TD,
  TD_MUTED,
  TD_NUM,
  TH,
  TH_NUM,
  THEAD,
  directorHref,
  useMoney,
} from '@/pages/cvm/shared';
import { cvmCaseParams, fetchCvmCases } from '@/services/cvm';
import type { CvmCaseRow } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * Every case the Colegiado judged, one row per case, its final observed extrato.
 *
 * The filters are read from the population being listed (a year offered here
 * has at least one judgment in it), and every one seeds from the URL because
 * the overview, the directors and the sanctions screens link here narrowed.
 * The accused column prints the names as CVM prints them; the fine column is
 * that judgment's own rows summed and never a party's total.
 */
export function CvmCasesPage() {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('q') ?? '');
  const [q, setQ] = useState<string | null>(seed.get('q') || null);
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [rite, setRite] = useState(seed.get('rite') ?? '');
  const [unit, setUnit] = useState(seed.get('unit') ?? '');
  const [outcome, setOutcome] = useState(seed.get('outcome') ?? '');
  const [sanctionType, setSanctionType] = useState(seed.get('sanction_type') ?? '');
  const [relator, setRelator] = useState(seed.get('relator') ?? '');

  const filters = {
    q,
    year: year ? Number(year) : null,
    rite: rite || null,
    unit: unit || null,
    outcome: outcome || null,
    sanction_type: sanctionType || null,
    relator: relator || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['cvm', 'cases', filters],
    queryFn: ({ pageParam }) => fetchCvmCases({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const options = data?.filters;
  const any = t('cvm:cases.filters.any');

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cvm:cases.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cvm:cases.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cvm:cases.explainer" />
      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQ(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="cvm-case-query" className="text-xs font-medium text-foreground">
            {t('cvm:cases.search.label')}
          </label>
          <input
            id="cvm-case-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('cvm:cases.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <Select
          id="cvm-year"
          label={t('cvm:cases.filters.year')}
          any={any}
          value={year}
          onChange={setYear}
          options={options?.years}
        />
        <Select
          id="cvm-rite"
          label={t('cvm:cases.filters.rite')}
          any={any}
          value={rite}
          onChange={setRite}
          options={options?.rites}
        />
        <Select
          id="cvm-unit"
          label={t('cvm:cases.filters.unit')}
          any={any}
          value={unit}
          onChange={setUnit}
          options={options?.units}
        />
        <Select
          id="cvm-outcome"
          label={t('cvm:cases.filters.outcome')}
          any={any}
          value={outcome}
          onChange={setOutcome}
          options={options?.outcomes}
          render={(option) => t(`cvm:outcomes.${option.value}`, { defaultValue: option.value })}
        />
        <Select
          id="cvm-sanction-type"
          label={t('cvm:cases.filters.sanctionType')}
          any={any}
          value={sanctionType}
          onChange={setSanctionType}
          options={options?.sanction_types}
          render={(option) =>
            t(`cvm:sanctionTypes.${option.value}`, {
              defaultValue: option.value,
            })
          }
        />
        <Select
          id="cvm-relator"
          label={t('cvm:cases.filters.relator')}
          any={any}
          value={relator}
          onChange={setRelator}
          options={options?.relators}
        />
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('cvm:cases.search.submit')}
        </button>
      </form>

      {data ? (
        <section className="rounded-lg border bg-card p-4" data-registry>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('cvm:cases.registry.title')}
          </h2>
          <Answer answer={data.registry} help={t('cvm:cases.registry.help')}>
            {(r) => (
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Figure label={t('cvm:cases.registry.cases')} value={number.format(r.cases)} />
                <Figure
                  label={t('cvm:cases.registry.withRelator')}
                  value={number.format(r.with_relator)}
                />
                <Figure
                  label={t('cvm:cases.registry.withSanctions')}
                  value={number.format(r.with_sanctions)}
                />
                <Figure
                  label={t('cvm:cases.registry.withVotes')}
                  value={number.format(r.with_votes)}
                />
                <Figure
                  label={t('cvm:cases.registry.withText')}
                  value={number.format(r.with_text)}
                />
                <Figure
                  label={t('cvm:cases.registry.withRegister')}
                  value={number.format(r.with_register)}
                />
                <Figure
                  label={t('cvm:cases.registry.withSettlement')}
                  value={number.format(r.with_settlement)}
                />
                <Figure
                  label={t('cvm:cases.registry.withAppeal')}
                  value={number.format(r.with_appeal)}
                />
              </dl>
            )}
          </Answer>
        </section>
      ) : null}

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/cvm/export/cases?${cvmCaseParams(filters).toString()}`} />
      </div>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : data.total === 0 ? (
        <EmptyState hint={t('cvm:list.empty')} />
      ) : (
        <>
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-total={data.total}
            data-stale={isPlaceholderData ? 'true' : 'false'}
          >
            {t('cvm:list.showing', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className={TABLE_REGION}
            role="region"
            aria-label={t('cvm:cases.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className={THEAD}>
                <tr>
                  <th className={TH}>{t('cvm:cases.columns.process')}</th>
                  <th className={TH}>{t('cvm:cases.columns.judgment')}</th>
                  <th className={TH}>{t('cvm:cases.columns.relator')}</th>
                  <th className={TH}>{t('cvm:cases.columns.accused')}</th>
                  <th className={TH}>{t('cvm:cases.columns.outcomes')}</th>
                  <th className={TH_NUM}>{t('cvm:cases.columns.fines')}</th>
                  <th className={TH}>{t('cvm:cases.columns.tracks')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <CaseRow key={row.case_key} row={row} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <LoadMore
              hasNextPage={Boolean(hasNextPage)}
              isFetchingNextPage={isFetchingNextPage}
              loadMore={loadMore}
              total={data.total}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}

      {data ? <PersonScopeCard answer={data.person_scope} /> : null}
      <FieldGuide i18nKey="cvm:cases.guide" />
    </div>
  );
}

function CaseRow({ row }: { row: CvmCaseRow }) {
  const { t } = useTranslation(['cvm']);
  const money = useMoney();
  const shown = row.accused.slice(0, 3);
  const more = row.accused_count - shown.length;
  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className={TD}>
        <Link
          to={`/cvm/c/${encodeURIComponent(row.case_key)}`}
          className="text-primary underline-offset-2 hover:underline"
        >
          {row.process_number}
        </Link>
        <div className="text-xs text-muted-foreground">
          {row.rite ?? ''}
          {row.rite && row.originating_unit ? ' · ' : ''}
          {row.originating_unit ?? ''}
        </div>
      </td>
      <td className={TD_MUTED}>
        <span className="tabular-nums text-foreground">{row.judgment_date ?? '—'}</span>
        {row.session_number ? (
          <div>{t('cvm:cases.sessionNumber', { number: row.session_number })}</div>
        ) : null}
      </td>
      <td className={TD}>
        <NameLink name={row.relator} href={directorHref(row.relator_slug)} />
      </td>
      <td className={TD}>
        <div className="text-xs text-foreground">{shown.join('; ') || '—'}</div>
        {more > 0 ? (
          <div className="text-xs text-muted-foreground">
            {t('cvm:cases.moreAccused', { count: more })}
          </div>
        ) : null}
      </td>
      <td className={TD_MUTED}>
        <div className="text-foreground">{t('cvm:cases.items', { count: row.items })}</div>
        <div>
          {row.outcome_codes.length
            ? row.outcome_codes
                .map((code) => t(`cvm:outcomes.${code}`, { defaultValue: code }))
                .join(' · ')
            : '—'}
        </div>
        <div>
          {row.sanction_types
            .map((code) => t(`cvm:sanctionTypes.${code}`, { defaultValue: code }))
            .join(' · ')}
        </div>
        {row.vote_mode_overall ? (
          <div>
            {t(`cvm:voteModes.${row.vote_mode_overall}`, {
              defaultValue: row.vote_mode_overall,
            })}
          </div>
        ) : null}
      </td>
      <td className={TD_NUM}>
        {row.fine_rows ? (
          <>
            <div>{money(row.fine_sum_brl)}</div>
            <div className="text-xs text-muted-foreground">
              {t('cvm:cases.fineRows', { count: row.fine_rows })}
            </div>
          </>
        ) : (
          '—'
        )}
      </td>
      <td className={TD_MUTED}>
        {[
          row.has_text_layer ? t('cvm:cases.tracks.text') : null,
          row.has_relatorio ? t('cvm:cases.tracks.relatorio') : null,
          row.in_register ? t('cvm:cases.tracks.register') : null,
          row.settlements ? t('cvm:cases.tracks.settlements', { count: row.settlements }) : null,
          row.appeals ? t('cvm:cases.tracks.appeals', { count: row.appeals }) : null,
        ]
          .filter(Boolean)
          .join(' · ') || '—'}
      </td>
    </tr>
  );
}
